import { useEffect } from "react";
import { ArrowRightLeft, Replace, X } from "lucide-react";
import { longDay } from "../utils/mealPlanUtils";

/* ════════════════════════════════════════════════════════════════════════
   MEAL MOVE CONFIRM MODAL
   Shown when a meal is dragged/dropped (or moved via action menu) onto a
   slot that already has a meal. Asks the user to Swap, Replace or Cancel.
   ════════════════════════════════════════════════════════════════════════ */

export function MealMoveConfirmModal({
  open,
  source,   // { day, slotId, slotLabel, slot }
  target,   // { day, slotId, slotLabel, slot }
  onSwap,
  onReplace,
  onClose,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open || !source || !target) return null;

  const srcTitle = source.slot?.title || source.slot?.recipeTitle || "Untitled meal";
  const tgtTitle = target.slot?.title || target.slot?.recipeTitle || "Untitled meal";

  return (
    <div
      className="fixed inset-0 z-[120] bg-slate-950/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      data-testid="meal-move-confirm-modal"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md rounded-3xl bg-white dark:bg-slate-900 shadow-none border border-[color:var(--ara-line-soft)] dark:border-rose-900/40 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3 flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl    flex items-center justify-center flex-shrink-0">
            <ArrowRightLeft className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-extrabold text-gray-900 dark:text-gray-50 leading-tight" data-testid="meal-move-confirm-title">
              This slot already has a meal
            </h2>
            <p className="text-xs font-bold text-gray-500 mt-0.5">Pick what should happen.</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 flex items-center justify-center text-gray-400"
            aria-label="Close"
            data-testid="meal-move-confirm-close"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* From → To summary */}
        <div className="px-5 pb-3 space-y-2">
          <FromToRow label="From" day={source.day} slotLabel={source.slotLabel} title={srcTitle} tone="amber" />
          <FromToRow label="To"   day={target.day} slotLabel={target.slotLabel} title={tgtTitle} tone="rose" />
        </div>

        <div className="px-5 pb-5 pt-1 flex flex-col gap-2">
          {onSwap && (
            <button
              onClick={onSwap}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-transparent hover:opacity-80 text-white text-sm font-extrabold shadow-sm active:scale-[0.98]"
              data-testid="meal-move-confirm-swap"
            >
              <ArrowRightLeft className="w-4 h-4" strokeWidth={1.5} /> Swap meals
            </button>
          )}
          <button
            onClick={onReplace}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-transparent hover:opacity-80 text-white text-sm font-extrabold shadow-sm active:scale-[0.98]"
            data-testid="meal-move-confirm-replace"
          >
            <Replace className="w-4 h-4" strokeWidth={1.5} /> {onSwap ? "Replace meal" : "Replace with recipe"}
          </button>
          <button
            onClick={onClose}
            className="w-full inline-flex items-center justify-center px-4 py-2.5 rounded-2xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-sm font-extrabold text-gray-700 dark:text-gray-200"
            data-testid="meal-move-confirm-cancel"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function FromToRow({ label, day, slotLabel, title, tone }) {
  const tones = {
    amber: "bg-transparent dark:bg-transparent border-[color:var(--ara-line-soft)] dark:border-amber-800/60 text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]",
    rose:  "bg-transparent dark:bg-transparent border-[color:var(--ara-line-soft)] dark:border-rose-800/60 text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]",
  };
  return (
    <div className={`rounded-2xl border px-3 py-2.5 ${tones[tone] || tones.amber}`}>
      <p className="text-[10px] font-extrabold uppercase tracking-wider">{label}</p>
      <p className="text-sm font-extrabold text-gray-900 dark:text-gray-50 mt-0.5">
        {longDay(day)} · {slotLabel}
      </p>
      <p className="text-xs font-bold text-gray-600 dark:text-gray-300 mt-0.5 truncate">{title}</p>
    </div>
  );
}

export default MealMoveConfirmModal;
